import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from "react";
import * as R from "ramda";

import useIsMounted from "hooks/useIsMounted";
import { AnsweredEntity, Answers, Field } from "types/entities";

type Action<T extends AnsweredEntity> =
  | { type: "loaded"; record: T }
  | { type: "setAnswer"; key: string; value: any }
  | { type: "clearAnswer"; key: string }
  | { type: "update"; record: T }
  | { type: "updateRecord"; record: T }
  | { type: "setError"; key: string; error: any }
  | { type: "clearError"; key: string }
  | { type: "clearErrors" };

const answerReducer = <T extends AnsweredEntity>(state: T, action: Action<T>): T => {
  switch (action.type) {
    case "loaded":
      return action.record;
    case "setAnswer": {
      const currentValue = R.path(["answers", action.key], state);
      if (R.equals(currentValue, action.value)) return state; // No change, return current state
      return R.assocPath(["answers", action.key], action.value, state);
    }
    case "clearAnswer":
      return R.dissocPath(["answers", action.key], state);
    case "update": {
      const existingAnswers = R.propOr({}, "answers", state) as Answers;
      const existingErrors = R.propOr({}, "errors", state);
      const recordWithErrors = R.assoc("errors", existingErrors, R.prop("record", action));
      const newAnswers = R.prop("answers", recordWithErrors);
      const mergedAnswers = R.mergeLeft(newAnswers, existingAnswers);

      const updatedRecord = R.assoc("answers", mergedAnswers, recordWithErrors) as T;
      if (R.equals(state, updatedRecord)) return state;
      return updatedRecord;
    }

    case "updateRecord": {
      const existingAnswers = R.propOr({}, "answers", state) as Answers;
      const updatedRecord = R.assoc("answers", existingAnswers, action.record) as T;
      if (R.equals(state, updatedRecord)) return state;
      return updatedRecord;
    }
    case "setError":
      return R.assocPath(["errors", action.key], action.error, state);

    case "clearError": {
      if (!R.path(["errors", action.key], state)) {
        return state;
      }

      return R.dissocPath(["errors", action.key], state);
    }

    case "clearErrors":
      return R.dissoc("errors", state) as T;

    default:
      throw new Error();
  }
};

interface InitialParams<T extends AnsweredEntity> {
  data?: T;
  isLoading?: boolean;
}

type SaveFunction<T extends AnsweredEntity> = (params: any) => Promise<T>;

interface Options {
  ephemeral?: boolean;
  debug?: boolean;
}

interface AfterSaveQueueItem<T extends AnsweredEntity> {
  fn: (record: T) => Promise<any>;
  validate?: any;
}

export interface AnswerContextManager<T extends AnsweredEntity> {
  record: T;
  onChange: (field: Field, value: any) => void;
  setError: (field: { key: string }, error: any) => void;
  clearError: (field: { key: string }) => void;
  onSave: (...fields: Field[]) => void;
  onAction: (fn: (record: T) => Promise<any>, options?: { validate?: any }) => void;
  pending: boolean;
  externalSetPending: (value: boolean) => void;
  setPending: Dispatch<SetStateAction<boolean>>;
  setSaveQueue: Dispatch<SetStateAction<string[]>>;
  changedFields: Field[];
  dispatch: Dispatch<Action<T>>;
  isLoading: boolean;
}

const useAnswerContextManager = <T extends AnsweredEntity>(
  { data: initialRecord = {} as T, isLoading = false }: InitialParams<T>,
  save: SaveFunction<T>,
  { ephemeral = false, debug = false }: Options = {},
): AnswerContextManager<T> => {
  const isMounted = useIsMounted();

  const [isLoaded, setIsLoaded] = useState(false);

  const [record, dispatch] = useReducer(answerReducer, initialRecord);
  const recordRef = useRef(record);
  useEffect(() => {
    recordRef.current = record;
  }, [record]);
  const [changedFields, setChangedFields] = useState<Field[]>([]);
  const lastModifiedTimes = useRef({});
  const lastSavedTimes = useRef({});

  const [pending, setPending] = useState(false);

  const [processing, setProccessing] = useState(false);
  const [saveQueue, setSaveQueue] = useState<string[]>([]);
  const [afterSaveQueue, setAfterSaveQueue] = useState<AfterSaveQueueItem<T>[]>([]);

  const consoleDebug = useCallback(
    (...args: any[]) => {
      if (debug) {
        console.log(...args);
      }
    },
    [debug],
  );

  useEffect(() => {
    const hasQueue = afterSaveQueue.length + saveQueue.length > 0;
    if (!processing) {
      setPending(hasQueue);
      if (hasQueue) runNext();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [afterSaveQueue, saveQueue, processing]);

  useEffect(() => {
    if (!isLoaded && !isLoading) {
      dispatch({ type: "loaded", record: initialRecord });
      setIsLoaded(true);
    }
  }, [isLoaded, isLoading, initialRecord]);

  const run = async (fn: () => Promise<any>) => {
    setPending(true);
    await fn();
    setProccessing(false);
  };

  const setErrors = (e: any) => {
    const errors = R.pathOr({}, ["response", "data", "errors"], e);

    R.forEachObjIndexed((error, key) => {
      dispatch({ type: "setError", key, error });
    }, errors);
  };

  const runNext = async () => {
    if (processing) {
      return;
    }
    consoleDebug("RUN NEXT");

    setProccessing(true);

    if (saveQueue.length > 0) {
      isMounted(() => {
        run(async () => {
          const fieldsToSave = saveQueue;
          const now = Date.now();
          fieldsToSave.forEach((key) => {
            lastSavedTimes.current[key] = now;
          });

          const answers = R.pickAll(fieldsToSave, R.propOr({}, "answers", recordRef.current));
          consoleDebug("SAVING", saveQueue);
          setSaveQueue([]);

          try {
            const result = (await save({ answers })) as T;
            const serverAnswers = R.propOr({}, "answers", result) as Answers;
            const currentAnswers = R.propOr({}, "answers", recordRef.current);

            const wasModifiedAfterSave = (key) => {
              if (key === "zoning") return false; // always favor server zoning answer
              const modifiedTime = R.propOr(0, key, lastModifiedTimes.current);
              const saveTime = R.propOr(0, key, lastSavedTimes.current);
              return modifiedTime > saveTime;
            };

            const modifiedFields = R.filter(wasModifiedAfterSave, R.keys(serverAnswers));
            consoleDebug("Modified after save", modifiedFields);
            const newerValues = R.pick(modifiedFields, currentAnswers);
            const mergedAnswers = R.mergeLeft(newerValues, serverAnswers);
            const updatedResult = R.assoc("answers", mergedAnswers, result);

            consoleDebug("UPDATED", R.pick(fieldsToSave, mergedAnswers));
            dispatch({ type: "update", record: updatedResult as T });
          } catch (e) {
            setErrors(e);
          }
        });
      });
    } else if (afterSaveQueue.length) {
      const nextAction = afterSaveQueue[0];
      setAfterSaveQueue(R.tail(afterSaveQueue));
      isMounted(async () => {
        run(async () => {
          let updatedRecord = record;
          if (nextAction.validate) {
            try {
              updatedRecord = await save({ validate_context: nextAction.validate });
              consoleDebug("VALIDATED", updatedRecord);
              dispatch({ type: "update", record: updatedRecord });
            } catch (e) {
              consoleDebug("ERRORS", e);
              setErrors(e);
              return;
            }
          }

          consoleDebug("RUN NEXT ACTION", nextAction);
          await nextAction.fn(updatedRecord as T);
        });
      });
    } else {
      setProccessing(false);
    }
  };
  const onChange = useCallback((field: Field, value: any) => {
    setChangedFields((fields) => R.uniqBy(R.prop("key"), fields.concat(field)));
    lastModifiedTimes.current[field.key] = Date.now();

    dispatch({ type: "clearError", key: field.key });
    dispatch({ type: "setAnswer", key: field.key, value });
  }, []);

  const clearError = useCallback(({ key }: { key: string }) => {
    dispatch({ type: "clearError", key });
  }, []);

  const setError = useCallback(({ key }: { key: string }, error: any) => {
    dispatch({ type: "setError", key, error });
  }, []);
  const onSave = useCallback(
    (...fields: Field[]) => {
      setSaveQueue((prevSaveQueue) => {
        const fieldsToSave = ephemeral ? [...changedFields, ...fields] : fields;
        return R.uniq([...prevSaveQueue, ...R.map(R.prop("key"), fieldsToSave)]);
      });
    },
    [changedFields, ephemeral],
  );
  const onAction = useCallback(
    (fn: (record: T) => Promise<any>, { validate = false } = {}) => {
      consoleDebug("ON ACTION", fn, validate);
      setPending(true);
      setAfterSaveQueue((prevAfterSaveQueue) => [...prevAfterSaveQueue, { fn, validate }]);
    },
    [consoleDebug],
  );

  const externalSetPending = useCallback((value: boolean) => {
    setPending(value);
    setProccessing(value);
  }, []);

  return {
    record: record as T,
    onChange,
    setError,
    clearError,
    onSave,
    onAction,
    pending,
    externalSetPending,
    setPending,
    setSaveQueue,
    changedFields,
    dispatch,
    isLoading: !isLoaded,
  };
};

export default useAnswerContextManager;
